import React from 'react'
import LandPageModal from '../Modal/LandPageModal'

type RematchAccent = 'amber' | 'red' | 'blue'

interface PvPBattleRematchModalProps {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  isLoading?: boolean
  accent?: RematchAccent
  countdownSeconds?: number | null
  disableClose?: boolean
  zIndex?: number
  /** When true, Accept is non-interactive (e.g. Insufficient GEM/USDT). */
  confirmDisabled?: boolean
  /** Shown under the buttons when confirm is disabled. */
  confirmDisabledHint?: string | null
}

const ACCENTS: Record<RematchAccent, { border: string; accent: string; close: string; title: string }> = {
  amber: {
    border: 'rgba(251, 191, 36, 0.45)',
    accent: 'rgba(251, 191, 36, 0.24)',
    close: 'rgba(251, 191, 36, 0.35)',
    title: '#fde68a',
  },
  red: {
    border: 'rgba(248, 113, 113, 0.45)',
    accent: 'rgba(248, 113, 113, 0.22)',
    close: 'rgba(248, 113, 113, 0.35)',
    title: '#fecaca',
  },
  blue: {
    border: 'rgba(96, 165, 250, 0.45)',
    accent: 'rgba(96, 165, 250, 0.22)',
    close: 'rgba(96, 165, 250, 0.35)',
    title: '#bfdbfe',
  },
}

export const PvPBattleRematchModal: React.FC<PvPBattleRematchModalProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading = false,
  accent = 'amber',
  countdownSeconds = null,
  disableClose = false,
  zIndex,
  confirmDisabled = false,
  confirmDisabledHint = null,
}) => {
  const palette = ACCENTS[accent]
  const acceptBlocked = confirmDisabled && !isLoading

  return (
    <LandPageModal
      open={open}
      onClose={disableClose ? (() => {}) : (onCancel || (() => {}))}
      width="92vw"
      maxWidth="520px"
      minHeight="260px"
      isLoading={isLoading}
      accentColor={palette.accent}
      closeButtonColor={palette.close}
      cornerColors={{
        topLeft: palette.border,
        topRight: palette.border,
        bottomLeft: palette.border,
        bottomRight: palette.border,
      }}
      hideCloseButton={disableClose}
      disableEscapeKeyDown={disableClose}
      contentNoScroll
      zIndex={zIndex}
    >
      <div
        className="flex min-h-[260px] flex-col justify-center px-5 py-8 text-center sm:px-8"
        style={{ fontFamily: 'Tilt Warp, sans-serif' }}
      >
        <h2 className="text-2xl font-bold sm:text-3xl" style={{ color: palette.title }}>
          {title}
        </h2>
        <p className="mt-4 text-sm leading-6 text-white/80 sm:text-base">{description}</p>
        {acceptBlocked && confirmDisabledHint ? (
          <p className="mt-3 text-xs leading-5 text-amber-200/90 sm:text-sm">{confirmDisabledHint}</p>
        ) : null}
        {typeof countdownSeconds === 'number' ? (
          <div className="mt-5 text-3xl font-bold text-white sm:text-4xl">{countdownSeconds}</div>
        ) : null}
        {(confirmLabel || cancelLabel) ? (
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            {cancelLabel && onCancel ? (
              <button
                onClick={onCancel}
                className="rounded-xl px-5 py-3 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] sm:text-base"
                style={{
                  background: 'linear-gradient(135deg, rgba(75, 85, 99, 0.82), rgba(31, 41, 55, 0.88))',
                  border: '2px solid rgba(148, 163, 184, 0.35)',
                }}
              >
                {cancelLabel}
              </button>
            ) : null}
            {confirmLabel && onConfirm ? (
              <button
                type="button"
                onClick={acceptBlocked ? undefined : onConfirm}
                disabled={acceptBlocked}
                title={acceptBlocked ? confirmDisabledHint || undefined : undefined}
                className={`rounded-xl px-5 py-3 text-sm font-bold text-white sm:text-base ${
                  acceptBlocked
                    ? 'cursor-not-allowed opacity-45'
                    : 'transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98]'
                }`}
                style={{
                  background:
                    accent === 'red'
                      ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.88), rgba(153, 27, 27, 0.88))'
                      : accent === 'blue'
                        ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.88), rgba(29, 78, 216, 0.88))'
                        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.88), rgba(217, 119, 6, 0.88))',
                  border: `2px solid ${palette.border}`,
                }}
              >
                {confirmLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </LandPageModal>
  )
}

export default PvPBattleRematchModal
